import React from 'react';
import SEO from '../../components/ui/SEO';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { FileCheck } from 'lucide-react';
export default function ClinicalNegligence() {
  return <main className="min-h-screen bg-white" data-id="element-951" data-mp="15dlxrsqrsqay">
      <SEO title="Clinical Negligence Psychiatric Reports | Causation" description="Independent psychiatric evidence on causation, psychiatric injury, condition and prognosis in clinical negligence claims. Instruct Dr Syed today." path="/services/clinical-negligence" keywords={['clinical negligence psychiatric expert witness', 'psychiatric injury medical negligence', 'condition and prognosis psychiatric report']} imageAlt="Clinical negligence psychiatric assessment reports" schema={{
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Clinical Negligence Psychiatric Assessments',
      serviceType: 'Clinical negligence psychiatric assessment',
      url: 'https://medicolegal-psychiatry.co.uk/services/clinical-negligence',
      areaServed: {
        '@type': 'AdministrativeArea',
        name: 'England and Wales'
      },
      provider: {
        '@type': 'Organization',
        name: 'Medicolegal Psychiatry',
        url: 'https://medicolegal-psychiatry.co.uk'
      },
      audience: {
        '@type': 'Audience',
        name: 'Legal professionals'
      },
      description: 'Psychiatric opinion on causation, psychiatric injury, condition and prognosis in clinical negligence proceedings.'
    }} data-id="element-952" data-mp="1abdo4ibro4d1" />

      {/* Hero Section */}
      <section className="bg-primary text-white py-10 pt-24 md:pt-28" data-id="element-953" data-mp="2ovmn3qwparj2">
        <div className="container mx-auto px-4" data-id="element-954" data-mp="32vnmf1xbhqwf">
          <div className="flex items-center justify-center mb-4" data-id="element-955" data-mp="32s9pi97fa9wd">
            <FileCheck className="h-12 w-12 mr-4" data-id="element-956" data-mp="2xx4379a3yt7r" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-957" data-mp="1vimzoe4u4kfe">
              Clinical Negligence Cases
            </h1>
          </div>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-958" data-mp="20hg1h9aremzq">
            Independent psychiatric assessments for clinical negligence matters
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12" data-id="element-959" data-mp="29bcmulw3nve3">
        <div className="container mx-auto px-4" data-id="element-960" data-mp="2e94edesu1fhr">
          <div className="max-w-3xl mx-auto" data-id="element-961" data-mp="2e8cpsnxnki0z">
            <div className="prose max-w-none" data-id="element-962" data-mp="2j765mn0ewl4b">
              {/* Intro */}
              <p className="text-slate-dark mb-6 text-lg" data-id="element-963" data-mp="2o6skt1zswbfr">
                We provide independent psychiatric assessments for clinical
                negligence matters where concerns have been raised about
                diagnosis, treatment, aftercare, risk management, or wider
                mental health care. Our reports are prepared to assist
                solicitors, courts, and tribunals by giving clear expert opinion
                on psychiatric injury, causation, impact, and prognosis.
              </p>

              {/* What This Service Covers */}
              <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-964" data-mp="lisjaomy3lzt">
                What This Service Covers
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-965" data-mp="cjozfifyf2k9">
                Our clinical negligence work may include cases involving:
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-966" data-mp="19965hyiumqmh">
                <li className="mb-2" data-id="element-967" data-mp="14ad1qxtd6dzi">
                  Delayed or missed diagnosis of psychiatric illness
                </li>
                <li className="mb-2" data-id="element-968" data-mp="107ge2soyvrop">
                  Inappropriate psychiatric treatment or medication management
                </li>
                <li className="mb-2" data-id="element-969" data-mp="vc9iy99rjvbe">
                  Failures in inpatient or community mental health care
                </li>
                <li className="mb-2" data-id="element-970" data-mp="3fzw5twpr7dwq">
                  Suicide and self-harm risk management concerns
                </li>
                <li className="mb-2" data-id="element-971" data-mp="17qo2mw5li42d">
                  Inadequate follow-up, discharge planning, or safeguarding
                </li>
                <li className="mb-2" data-id="element-972" data-mp="1ck0owzmsxyry">
                  Deterioration in mental health linked to alleged negligent
                  care
                </li>
                <li data-id="element-973" data-mp="12t60bom32n3c">
                  Capacity, vulnerability, and psychiatric consequences arising
                  from poor care
                </li>
              </ul>

              {/* How We Assist */}
              <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-974" data-mp="1vgicd4umbcie">
                How We Assist
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-975" data-mp="1hjlmp6q8k133">
                Our role is to provide balanced, independent psychiatric opinion
                on the mental health aspects of a clinical negligence claim.
                This may include reviewing medical records, assessing the
                individual, considering breach and causation issues where
                appropriate, and setting out the psychiatric consequences in a
                clear and structured report.
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-976" data-mp="3uqbi5e3h9vcf">
                <li className="mb-2" data-id="element-977" data-mp="chu607twkly8">
                  Review of psychiatric and relevant medical records
                </li>
                <li className="mb-2" data-id="element-978" data-mp="7hg8vktuawez">
                  Independent assessment and expert opinion
                </li>
                <li className="mb-2" data-id="element-979" data-mp="3b09jborbn54x">
                  Consideration of causation and psychiatric impact
                </li>
                <li className="mb-2" data-id="element-980" data-mp="3pp5rrmxaik62">
                  Diagnosis, prognosis, and treatment recommendations where
                  required
                </li>
                <li data-id="element-981" data-mp="2x8l7znowckp0">
                  Clear reporting suitable for litigation and expert witness
                  purposes
                </li>
              </ul>

              {/* Who This Service Is For */}
              <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-982" data-mp="w6lo0ee0p1ho">
                Who This Service Is For
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-983" data-mp="1145dybk2v2pg">
                This service is designed for solicitors, legal teams, and
                organisations requiring expert psychiatric input in clinical
                negligence matters.
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-984" data-mp="d7hllogze5yz">
                <li className="mb-2" data-id="element-985" data-mp="d9c7p63zo44n">Claimant solicitors</li>
                <li className="mb-2" data-id="element-986" data-mp="i7m0ja28qsn3">Defendant solicitors</li>
                <li className="mb-2" data-id="element-987" data-mp="m85wiab2vq7n">Courts and tribunals</li>
                <li data-id="element-988" data-mp="m6mi6p8zfp8i">Agencies instructing psychiatric experts</li>
              </ul>

              {/* Why Instruct Medicolegal Psychiatry */}
              <h2 className="text-2xl font-serif font-bold text-primary mb-6" data-id="element-989" data-mp="1xncos9hbij2n">
                Why Instruct Medicolegal Psychiatry
              </h2>
              <p className="text-slate-dark mb-4" data-id="element-990" data-mp="22kuqpfwx3y15">
                Medicolegal Psychiatry provides specialist psychiatric expertise
                with a clear focus on independence, clarity, and reliability.
                Reports are prepared with the needs of legal professionals in
                mind and written in a structured format suitable for scrutiny.
              </p>
              <ul className="list-disc pl-6 mb-6 text-slate-dark" data-id="element-991" data-mp="1yxwoc4obdhyy">
                <li className="mb-2" data-id="element-992" data-mp="1tzle3su3x5g7">Independent and evidence-based opinion</li>
                <li className="mb-2" data-id="element-993" data-mp="1txiech35jxya">
                  Clear, well-structured psychiatric reporting
                </li>
                <li className="mb-2" data-id="element-994" data-mp="1k1fexizoqdf1">
                  Experience in complex medico-legal matters
                </li>
                <li className="mb-2" data-id="element-995" data-mp="1g156lzof7ax0">Professional, responsive communication</li>
                <li data-id="element-996" data-mp="3eg85h3lpoe04">Reports tailored to the needs of litigation</li>
              </ul>

              {/* Related Services */}
              <div className="mb-10 border-t border-gray-200 pt-8" data-id="element-997" data-mp="3af6168cxtbra">
                <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-998" data-mp="3tn4jrw1gk9ib">
                  Explore Our Other Services
                </h3>
                <div className="flex flex-col sm:flex-row flex-wrap gap-4" data-id="element-999" data-mp="1ikaxzfsvtok">
                  <Link to="/services/mental-capacity" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1000" data-mp="3jw09h9mq9ctt">
                    Mental Capacity Assessments
                  </Link>
                  <Link to="/services/trauma-ptsd" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1001" data-mp="3exq98km5bm1c">
                    PTSD & Trauma Psychiatric Evaluations
                  </Link>
                  <Link to="/services/specialist" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1002" data-mp="35z4uz1npit6d">
                    Psychiatric Reports for Courts & Tribunals
                  </Link>
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-gray-50 rounded-lg p-6 shadow-md mb-8" data-id="element-1003" data-mp="19c2fft4oqlxm">
                <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1004" data-mp="1n2fipi6mtcgm">
                  Need a Psychiatric Assessment for a Clinical Negligence
                  Matter?
                </h3>
                <p className="text-slate-dark mb-4" data-id="element-1005" data-mp="1wzk8drzorq26">
                  If you require an independent psychiatric expert for a
                  clinical negligence case, please get in touch to discuss the
                  instruction, timescales, and scope of assessment.
                </p>
                <Button variant="primary" href="/contact" data-id="element-1006" data-mp="21z6me3nc9ac1">
                  Contact Us
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}